const CONTENTFUL_LOCALE_KEYS = new Set(['en-US', 'de', 'en']);

function unwrapLocalized(value, locale) {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const keys = Object.keys(value);
  if (keys.length > 0 && keys.every((key) => CONTENTFUL_LOCALE_KEYS.has(key))) {
    return value[locale] ?? value.de ?? value['en-US'] ?? value.en;
  }
  return value;
}

export const COPY_NAMESPACES = [
  'common',
  'validation',
  'toasts',
  'emptyStates',
  'errors',
  'nav',
  'auth',
  'dashboard',
  'talents',
  'help',
  'account',
  'assessments',
  'dataShare',
];

function typeId(entry) {
  return entry?.sys?.contentType?.sys?.id;
}

function fieldsOf(entry) {
  return entry?.fields || {};
}

function asList(value) {
  if (Array.isArray(value)) return value;
  return [];
}

export function mapContentfulEntries(
  { copyBundles = [], catalogs = [] } = {},
  contentfulLocale = 'en-US'
) {
  const overlay = {
    catalogs: {
      competencies: {},
      motivations: {},
      personality: {},
      teamStrengths: {},
      salesStrengths: {},
      adept: { aspects: {}, styles: {} },
      assessments: {},
      reports: {},
    },
  };

  for (const entry of copyBundles) {
    const fields = fieldsOf(entry);
    const app = unwrapLocalized(fields.app, contentfulLocale);
    if (app !== 'talents') continue;
    const namespace = unwrapLocalized(fields.namespace, contentfulLocale);
    if (!namespace || !COPY_NAMESPACES.includes(namespace)) continue;
    const tree = unwrapLocalized(fields.tree, contentfulLocale);
    if (tree && typeof tree === 'object') {
      overlay[namespace] = tree;
    }
  }

  for (const entry of catalogs) {
    const fields = fieldsOf(entry);
    switch (typeId(entry)) {
      case 'competency': {
        const id = fields.apiId;
        if (id == null) break;
        overlay.catalogs.competencies[String(id)] = {
          id,
          slug: fields.slug,
          title: fields.title,
          description: fields.description,
          summary: fields.summary,
          howToLead: asList(fields.howToLead),
          howToDevelop: asList(fields.howToDevelop),
        };
        break;
      }
      case 'motivation': {
        const id = fields.apiId;
        if (id == null) break;
        overlay.catalogs.motivations[String(id)] = {
          id,
          slug: fields.slug,
          title: fields.title,
          lowDescription: fields.lowDescription,
          highDescription: fields.highDescription,
          summary: fields.summary,
          high: asList(fields.high),
          low: asList(fields.low),
          howToManage: asList(fields.howToManage),
          watchOut: asList(fields.watchOut),
        };
        break;
      }
      case 'personalityDimension': {
        const slug = fields.slug;
        if (!slug) break;
        overlay.catalogs.personality[slug] = {
          id: slug,
          title: fields.title,
          summary: fields.summary,
          sampleItems: asList(fields.sampleItems),
          high: asList(fields.high),
          low: asList(fields.low),
        };
        break;
      }
      case 'teamStrength': {
        const slug = fields.slug;
        if (!slug) break;
        overlay.catalogs.teamStrengths[slug] = {
          id: slug,
          index: fields.sortOrder,
          title: fields.title,
          summary: fields.summary,
          high: asList(fields.high),
          low: asList(fields.low),
        };
        break;
      }
      case 'salesStrength': {
        const slug = fields.slug;
        if (!slug) break;
        overlay.catalogs.salesStrengths[slug] = {
          id: slug,
          index: fields.sortOrder,
          title: fields.title,
          summary: fields.summary,
          high: asList(fields.high),
          low: asList(fields.low),
        };
        break;
      }
      case 'adeptAspect': {
        const slug = fields.slug;
        if (!slug) break;
        overlay.catalogs.adept.aspects[slug] = {
          id: slug,
          title: fields.title,
          definition: fields.definition,
          high: asList(fields.high),
          low: asList(fields.low),
        };
        break;
      }
      case 'adeptStyle': {
        const slug = fields.slug;
        if (!slug) break;
        overlay.catalogs.adept.styles[slug] = {
          id: slug,
          title: fields.title,
          description: fields.description,
          aspectIds: asList(fields.aspectSlugs),
        };
        break;
      }
      case 'assessmentInstrument': {
        const id = fields.instrumentId;
        if (id == null || Number(id) === 357) break;
        const item = {
          id,
          name: fields.name,
          tooltip: fields.tooltip,
        };
        if (fields.profileDescription != null) {
          item.profileDescription = fields.profileDescription;
        }
        overlay.catalogs.assessments[String(id)] = item;
        break;
      }
      case 'report': {
        const id = fields.reportId;
        if (id == null || Number(id) === 357002 || Number(fields.instrumentId) === 357) break;
        overlay.catalogs.reports[String(id)] = {
          id,
          instrumentId: fields.instrumentId,
          name: fields.name,
          languages: fields.languages || [],
        };
        break;
      }
      default:
        break;
    }
  }

  return overlay;
}

export function deepMerge(base, overlay) {
  if (overlay == null) return base;
  if (Array.isArray(overlay)) return overlay;
  if (typeof overlay !== 'object') return overlay;
  if (typeof base !== 'object' || base == null || Array.isArray(base)) return overlay;
  const next = { ...base };
  Object.keys(overlay).forEach((key) => {
    next[key] = key in base ? deepMerge(base[key], overlay[key]) : overlay[key];
  });
  return next;
}

export function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}
